// First-run screens: the channel-join gate and the intro screen.
import { useState, useEffect, useRef } from "react";
import { Play, Pause, Music } from "lucide-react";
import { useLang } from "../../i18n.jsx";
import { FluentEmoji, Stars } from "../emoji.jsx";
import { fmt } from "../format.js";
import { PLATFORM_FALLBACK, openExternal, platformInfo } from "../platform.js";
import { C } from "../../design-system.js";
import { AudioProgress } from "../ui.jsx";

// ── Channel Join Screen (shown to non-members) ──────────────────────────────
// A public sample story so prospective users can HEAR the quality before joining.
// Stored under /media/samples/ (stable — exempt from the retention purge).
// Sample story shown on the channel-join screen — a real generated story per language.
export const SAMPLE_STORY_FA = { title: "نگار و ماجرای جوجه‌اردک در باغ بهار", url: "https://imaaa.ir/media/samples/duckling.mp3" };
export const SAMPLE_STORY_EN = { title: "The Dance of Silver Lights on the Moonlit River", url: "https://imaaa.ir/media/samples/moonlight-en.mp3" };
export const sampleStory = (lang) => (lang === "en" ? SAMPLE_STORY_EN : SAMPLE_STORY_FA);

export function SampleStoryCard() {
  const { t, lang } = useLang();
  const SAMPLE_STORY = sampleStory(lang);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [dur, setDur] = useState(0);
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    const onTime = () => setElapsed(a.currentTime || 0);
    const d = () => { if (isFinite(a.duration)) setDur(a.duration); };
    const end = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime); a.addEventListener("loadedmetadata", d);
    a.addEventListener("durationchange", d); a.addEventListener("ended", end);
    return () => { a.removeEventListener("timeupdate", onTime); a.removeEventListener("loadedmetadata", d); a.removeEventListener("durationchange", d); a.removeEventListener("ended", end); };
  }, []);
  useEffect(() => {
    const a = audioRef.current; if (!a) return;
    if (playing) a.play().catch(() => setPlaying(false)); else a.pause();
  }, [playing]);
  const pct = dur ? (elapsed / dur) * 100 : 0;
  const fmt = s => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const seek = fr => { const a = audioRef.current; if (a && dur) { a.currentTime = fr * dur; setElapsed(a.currentTime); } };
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.tealDim}`, borderRadius: 18, padding: 16, marginBottom: 20, animation: "fadeIn 0.5s ease" }}>
      <audio ref={audioRef} src={SAMPLE_STORY.url} preload="none" />
      <div style={{ color: C.teal, fontSize: 12.5, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
        <Music size={14} /> {t("یک قصهٔ نمونه را بشنوید")}
      </div>
      <div style={{ color: C.cream, fontSize: 14.5, fontWeight: 700, textAlign: "center", marginBottom: 14, lineHeight: 1.6 }}>{SAMPLE_STORY.title}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, direction: "ltr" }}>
        <button onClick={() => setPlaying(!playing)} className="press" aria-label={playing ? t("توقف") : t("پخش")}
          style={{ width: 46, height: 46, borderRadius: 23, background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, border: "none", color: C.bgDeep, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 3px 14px ${C.gold}44` }}>
          {playing ? <Pause size={20} fill={C.bgDeep} /> : <Play size={20} fill={C.bgDeep} style={{ marginLeft: 2 }} />}
        </button>
        <AudioProgress pct={pct} onSeekFrac={seek} />
        <span style={{ color: C.textMid, fontSize: 11, fontVariantNumeric: "tabular-nums", flexShrink: 0, minWidth: 34, textAlign: "center" }}>{dur ? fmt(dur - elapsed) : (lang === "en" ? "0:00" : "۶:۴۷")}</span>
      </div>
    </div>
  );
}

// There is NO «عضو شدم» button any more (product owner, 2026-10-27): membership is
// CHECKED, so tapping the join link is enough — the shell re-asks the server when
// the parent comes back (and every few seconds meanwhile) and goes straight in once
// they are a member. On a messenger that can't verify membership (Eitaa, Soroush)
// the tap itself is taken on trust, exactly as the old button's tap was.
export function ChannelJoinScreen({ onJoinTapped, platform, watching = false }) {
  const { t } = useLang();
  // The channel to join is the one for THIS messenger.
  //
  // ⚠️ `platform` MUST arrive as a PROP (React state owned by App), not by calling
  // platformInfo() here. This screen is the app's INITIAL screen, so it mounts
  // before /auth/login returns; reading sessionStorage at render time gave the
  // pre-login fallback, and the later setScreen("welcome") is a no-op (same value)
  // so React never reliably re-rendered it. That is why Telegram users kept being
  // sent to ble.ir even after the link was "fixed". The prop makes the update
  // deterministic; platformInfo() stays only as the first-paint default.
  const p = platform || platformInfo();
  const joinUrl = p.channel_url
    || PLATFORM_FALLBACK[p.key]?.channel_url
    || PLATFORM_FALLBACK.bale.channel_url;
  return (
    <div style={{ minHeight: "100vh", background: C.bgDeep, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, position: "relative", overflow: "hidden" }}>
      <Stars count={60} />
      <div style={{ position: "absolute", top: -80, right: -80, width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, ${C.goldDim}55 0%, transparent 70%)`, pointerEvents: "none" }} />
      <div style={{ position: "relative", textAlign: "center", maxWidth: 340 }}>
        <FluentEmoji anim="floatBig" size={80} style={{ marginBottom: 8, filter: `drop-shadow(0 0 24px ${C.gold}88)` }}>🌙</FluentEmoji>
        <h1 style={{ color: C.gold, fontSize: 30, fontWeight: 800, margin: "0 0 8px", letterSpacing: 1 }}>{t("ضحا قصه")}</h1>
        <p style={{ color: C.cream, fontSize: 15, lineHeight: 1.8, margin: "0 0 20px" }}>
          {t("داستان‌های شخصی‌سازی‌شده برای کودک شما")}
        </p>
        <SampleStoryCard />
        <div style={{ background: C.bgCard, border: `1px solid ${C.borderGold}`, borderRadius: 20, padding: 20, animation: "fadeIn 0.5s ease" }}>
          <p style={{ color: C.cream, fontSize: 15, lineHeight: 1.8, margin: "0 0 16px" }}>
            {t("برای استفاده از ضحا قصه، ابتدا در کانال ما عضو شوید")}
          </p>
          {/* href kept for semantics/long-press, but the click goes through the
              host bridge — target="_blank" alone does nothing in a messenger WebView. */}
          <a href={joinUrl} target="_blank" rel="noreferrer"
            onClick={(e) => { e.preventDefault(); openExternal(joinUrl); onJoinTapped?.(); }}
            className="press"
            style={{ display: "block", background: `linear-gradient(135deg, ${C.gold}, ${C.goldLight})`, color: C.bgDeep, fontWeight: 800, fontSize: 17, padding: "15px 0", borderRadius: 14, textDecoration: "none", boxShadow: `0 4px 20px ${C.gold}44` }}>
            {t("عضویت در کانال ضحا قصه 🌙")}
          </a>
          <p style={{ color: watching ? C.teal : C.textMid, fontSize: 13.5, lineHeight: 1.8, margin: "12px 0 0", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {watching && <span className="spin" style={{ width: 12, height: 12, borderRadius: "50%", border: `2px solid ${C.teal}55`, borderTopColor: C.teal, display: "inline-block", animation: "spin 0.9s linear infinite", flexShrink: 0 }} />}
            {watching ? t("پس از عضویت، خودکار وارد می‌شوید…") : t("بعد از عضویت به همین‌جا برگردید؛ خودکار وارد می‌شوید.")}
          </p>
        </div>
      </div>
    </div>
  );
}
